import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Copy, FolderSymlink, Trash2, TriangleAlert } from "lucide-react"

import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { declararFuente, fetchFuentes, quitarFuente } from "@/routes/onboarding/api"
import { t } from "@/lib/i18n"

/**
 * DE DONDE VIENEN LOS DOCUMENTOS.
 *
 * Vive dentro de la pantalla de primeros pasos y no detras de un enlace porque
 * es el unico paso cuya accion no existe en ningun otro sitio del producto.
 *
 * LAS DOS MITADES, Y LA SEGUNDA NO ES AQUI. Declarar la carpeta es un dato que
 * se guarda aqui; **darle permiso es en Google**. Una carpeta declarada y sin
 * compartir se ve identica a una que funciona, hasta que la sincronizacion trae
 * cero archivos y nadie sabe si es que no hay documentos o que no hay permiso.
 * Por eso la pantalla muestra CON QUIEN hay que compartirla, con un boton para
 * copiar ese correo, y marca la fuente como «sin comprobar» hasta que alguien
 * lo pruebe.
 *
 * Y SI NO HAY CUENTA DE SERVICIO, SE DICE. Mostrar «comparte con» seguido de un
 * hueco es peor que no mostrar nada: se copia el vacio y se comparte con nadie.
 *
 * `embebido` la mete DENTRO del paso «Donde guardas tus documentos», sin su
 * propia hoja ni su propio titulo. La seccion suelta existia porque antes ningun
 * paso podia rellenarse en sitio; ahora el paso es su sitio, y una hoja con
 * titulo dentro de otra ficha con titulo repite la misma cabecera dos veces.
 */
export function FuentesDocumentales({ embebido = false }: { embebido?: boolean } = {}) {
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const q = useQuery({ queryKey: ["fuentes-documentales"], queryFn: fetchFuentes })
  const [enlace, setEnlace] = React.useState("")
  const [etiqueta, setEtiqueta] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const correo = q.data?.correo_de_servicio ?? null

  /**
   * VARIAS CARPETAS DE UNA VEZ.
   *
   * Cesar, punto 15: «en el punto 3 de onboarding deberías de poder subir más de
   * una sola carpeta».
   *
   * Y aquí está lo interesante: **varias ya se podían**, de una en una — la lista
   * de arriba admite todas las que quieras y el backend sólo rechaza la repetida.
   * Lo que no se podía era VERLO: un campo de una línea con un botón que dice
   * «Declarar carpeta», en singular, no dice en ninguna parte que se puede volver
   * a usar. Cesar no se equivocó leyendo la pantalla; la pantalla le dijo eso.
   *
   * Así que no se añade una capacidad, se hace visible: un área de varias líneas,
   * una carpeta por línea, y el botón dice cuántas va a declarar. Quien tenga una
   * la pega igual y no nota la diferencia.
   *
   * SE DECLARAN UNA POR UNA Y NO EN PARALELO, y el resultado se cuenta por
   * separado: el endpoint rechaza la repetida con un 409, que NO es un error sino
   * la respuesta correcta —«esa ya la tengo»—. En paralelo, un 409 de la tercera
   * abortaría las otras cuatro y no habría forma de saber cuáles entraron.
   */
  const lineas = React.useMemo(
    () => enlace.split("\n").map((l) => l.trim()).filter((l) => l.length >= 8),
    [enlace],
  )

  const declarar = useMutation({
    mutationFn: async () => {
      let nuevas = 0
      const repetidas: string[] = []
      const fallidas: string[] = []
      for (const [i, referencia] of lineas.entries()) {
        try {
          await declararFuente({
            tipo: "drive",
            referencia,
            // La etiqueta se numera cuando hay varias: tres carpetas llamadas
            // igual en la lista no se distinguen, y el nombre es lo único que se
            // ve de ellas.
            etiqueta: etiqueta
              ? lineas.length > 1 ? `${etiqueta} (${i + 1})` : etiqueta
              : null,
            motivo: "Origen de los documentos declarado en el onboarding.",
          })
          nuevas++
        } catch (e) {
          const status = (e as { status?: number }).status
          if (status === 409) repetidas.push(referencia)
          else fallidas.push(referencia)
        }
      }
      return { nuevas, repetidas, fallidas }
    },
    onSuccess: (r) => {
      const partes = [`${r.nuevas} declarada(s)`]
      if (r.repetidas.length) partes.push(`${r.repetidas.length} que ya tenía`)
      if (r.fallidas.length) partes.push(`${r.fallidas.length} que no pude leer`)
      toast({
        variant: r.fallidas.length ? "warning" : undefined,
        title: r.nuevas ? t("Carpetas declaradas") : t("No había nada nuevo"),
        description: partes.join(" · "),
      })
      if (r.nuevas) {
        setEnlace("")
        setEtiqueta("")
      }
      void qc.invalidateQueries({ queryKey: ["fuentes-documentales"] })
      void qc.invalidateQueries({ queryKey: ["onboarding-pasos"] })
    },
    onError: onWriteError("las carpetas", "declarar"),
  })

  const quitar = useMutation({
    mutationFn: quitarFuente,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fuentes-documentales"] })
      void qc.invalidateQueries({ queryKey: ["onboarding-pasos"] })
    },
    onError: onWriteError("la carpeta", "quitar"),
  })

  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto)
      toast({ title: "Copiado" })
    } catch {
      // El portapapeles falla sin https o sin permiso. Se dice, en vez de
      // fingir que se copio y dejar a alguien pegando lo que tenia antes.
      toast({
        variant: "warning",
        title: "No se pudo copiar",
        description: "Selecciónalo y cópialo a mano.",
      })
    }
  }

  const fuentes = q.data?.fuentes ?? []

  const cuerpo = (
    <>
        {fuentes.length > 0 ? (
          <ul className="mb-3 space-y-1.5">
            {fuentes.map((f) => (
              <li
                key={f.id}
                className="flex flex-wrap items-center gap-2 rounded-md border px-2.5 py-1.5"
              >
                <FolderSymlink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-xs" title={f.referencia ?? ""}>
                  {f.etiqueta || f.referencia || f.tipo}
                </span>
                <Badge variant="outline" className="code">
                  {f.tipo}
                </Badge>
                {f.verificado ? (
                  <Badge variant="success-soft" estado>
                    <Check />
                    {t("acceso comprobado")}</Badge>
                ) : (
                  <Badge variant="warning-soft" estado>
                    <TriangleAlert />
                    {t("sin comprobar")}</Badge>
                )}
                {puedeEditar ? (
                  <Button
                    size="iconSm"
                    variant="ghost"
                    aria-label={t("Quitar")}
                    disabled={quitar.isPending}
                    onClick={() => quitar.mutate(f.id)}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {puedeEditar ? (
          <>
            <ol className="mb-2.5 space-y-1.5 text-2xs leading-5">
              <li>
                <strong className="font-medium">1.</strong> {" "}{t("Pégame los enlaces de las carpetas de Google Drive donde ya guardas tus comprobantes —puedes poner varias, una por línea—. La barra de direcciones entera vale: no hace falta que extraigas ningún identificador.")}</li>
              <li>
                <strong className="font-medium">2.</strong> {" "}{t("En Google, compártemela")}{" "}
                <strong className="font-medium">{t("en modo lector")}</strong> con{" "}
                {correo ? (
                  <>
                    <span className="code">{correo}</span>
                    <Button
                      size="xs"
                      variant="ghost"
                      className="ml-1 align-baseline"
                      onClick={() => void copiar(correo)}
                    >
                      <Copy />
                      copiar
                    </Button>
                  </>
                ) : (
                  <span className="text-warning">
                    {t("la cuenta de servicio del sistema —que todavía no está configurada en este servidor, así que hay que pedirla antes de poder compartir")}</span>
                )}
                .
              </li>
            </ol>

            <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,14rem)] md:items-start">
              <div className="space-y-1">
                <Label htmlFor="drive-enlace">
                  {t("Enlaces de las carpetas · una por línea")}</Label>
                <Textarea
                  id="drive-enlace"
                  value={enlace}
                  rows={3}
                  onChange={(e) => setEnlace(e.target.value)}
                  placeholder={"https://drive.google.com/drive/folders/…\nhttps://drive.google.com/drive/folders/…"}
                  className="text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="drive-etiqueta">{t("Cómo llamarlas")}</Label>
                <Input
                  id="drive-etiqueta"
                  value={etiqueta}
                  onChange={(e) => setEtiqueta(e.target.value)}
                  placeholder={t("Contabilidad 2024-2026")}
                  className="text-xs"
                />
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={lineas.length === 0 || declarar.isPending}
                onClick={() => declarar.mutate()}
              >
                <FolderSymlink />
                {declarar.isPending
                  ? t("Guardando…")
                  : lineas.length > 1
                    ? `${t("Declarar")} ${lineas.length} ${t("carpetas")}`
                    : t("Declarar la carpeta")}
              </Button>
              {lineas.length > 1 ? (
                <span className="text-2xs text-muted-foreground">
                  {t("Las repetidas no se duplican: te digo cuáles ya tenía y sigo.")}</span>
              ) : null}
            </div>

            <p className={cn("mt-2 text-2xs leading-5 text-muted-foreground")}>
              {t("¿Prefieres mandármelos a mano? También vale. Declararme la carpeta sólo te ahorra volver a subirlos cada mes.")}</p>
          </>
        ) : null}
    </>
  )

  if (embebido) {
    return (
      <div id="fuentes" className="scroll-mt-4 rounded-md border bg-surface-sunken/40 px-2.5 py-2.5">
        {cuerpo}
        {q.data?.nota ? (
          <p className="mt-2 border-t pt-2 text-2xs leading-4 text-muted-foreground">
            {q.data.nota}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <section id="fuentes" className="mt-5 scroll-mt-4">
      <WorkPanel
        title={t("De dónde vienen tus documentos")}
        meta={
          fuentes.length ? (
            <Badge variant="outline">{fuentes.length}</Badge>
          ) : (
            <Badge variant="outline">{t("sin declarar")}</Badge>
          )
        }
        footnote={q.data?.nota}
        bodyClassName="p-2.5"
      >
        {cuerpo}
      </WorkPanel>
    </section>
  )
}
